import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";

import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";

export const PatientLogin = () => {
  const navigate = useNavigate();
  const { loginWithCredentials } = useAuth();

  const [checked, setChecked] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [localPhone, setLocalPhone] = useState("");
  const [countryCode, setCountryCode] = useState("");
  // Signup popup
  const [openSignup, setOpenSignup] = useState(false);

  // Signup form
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    userId: "",
    password: "",
    dob: "",
    gender: "",
    email: "",
  });

  const validateSignupForm = () => {
    if (!form.firstName.trim()) return "First name is required";
    if (!form.lastName.trim()) return "Last name is required";
    if (!form.userId.trim()) return "User ID is required";
    if (!form.email.trim()) return "Email is required";
    if (!form.password.trim()) return "Password is required";
    if (!form.dob) return "Date of birth is required";
    if (!form.gender) return "Gender is required";

    if (!countryCode) return "Country code is required";
    if (!localPhone) return "Mobile number is required";

    // Optional strong validations
    if (localPhone.length < 8) return "Invalid mobile number";

    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Invalid email address";

    if (form.password.length < 6)
      return "Password must be at least 6 characters";

    return null; // ✅ valid
  };

  /* ===============================
     Handlers
  =============================== */
  const handleSignupChange = (e: any) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    try {
      await loginWithCredentials(email, password, "patient");
      navigate("/user-layout");
    } catch (err: any) {
      toast.error(err?.message || "Invalid credentials");
    }
  };

  const handleChange = (value: string, meta: any) => {
    setPhone(value);

    if (meta?.country?.dialCode) {
      const dial = `+${meta.country.dialCode}`;

      setCountryCode(dial);
      setLocalPhone(value.replace(dial, ""));
    }
  };

  const handleSignupSubmit = async () => {
    const error = validateSignupForm();

    if (error) {
      toast.error(error);
      return;
    }

    try {
      const response = await fetch(
        "https://vivotal-backend.vercel.app/api/v1/auth/register",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            first_name: form.firstName,
            last_name: form.lastName,
            username: form.userId,
            email: form.email,
            password: form.password,
            birth_date: form.dob,
            gender: form.gender,
            contact_number: localPhone,
            country_code: countryCode,
            role_type: "PATIENT",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        toast.error(data?.data?.errorMessage || "Signup failed");
        return;
      }

      toast.success("Signup successful! Please login.");
      setOpenSignup(false);
    } catch {
      toast.error("Something went wrong!");
    }
  };

  return (
    <>
      {/* ================= LOGIN ================= */}
      <div className="LoginWrap">
        <div className="imageContainer" style={{ padding: 20, color: "#fff" }}>
          <div style={{ fontWeight: 500, fontSize: 22, textAlign: "center" }}>
            With Your Health File patient portal, your path to wellness just got
            simpler
          </div>
          <div style={{ textAlign: "center", padding: 40 }}>
            <img src="pbg.png" width="100%" alt="Decorative background" />
          </div>
        </div>

        <div className="inputField">
          <div className="inputFieldWrap">
            <div className="specialLabel">Vivotal</div>

            <div style={{ marginBottom: 20, fontWeight: 600, fontSize: 27 }}>
              Patient Login
            </div>

            <div className="input-field">
              <label>Email</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>

            <div className="input-field">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div
              className="input-field"
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <Switch
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                />
                <label>Remember me</label>
              </div>

              <button
                style={{ background: "none", border: "none", color: "#009FF5" }}
              >
                Forgot Password?
              </button>
            </div>

            <button className="large-bttn" onClick={handleSubmit}>
              Sign In
            </button>

            <div style={{ textAlign: "center", marginTop: 20 }}>
              Don&apos;t have an account?{" "}
              <button
                onClick={() => setOpenSignup(true)}
                style={{ background: "none", border: "none", color: "#009FF5" }}
              >
                Sign up
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ================= SIGNUP DIALOG ================= */}
      <Dialog
        open={openSignup}
        onClose={() => setOpenSignup(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Sign Up</DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="First Name"
            name="firstName"
            onChange={handleSignupChange}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Last Name"
            name="lastName"
            onChange={handleSignupChange}
          />
          <TextField
            fullWidth
            margin="dense"
            label="User ID"
            name="userId"
            onChange={handleSignupChange}
          />
          <TextField
            fullWidth
            margin="dense"
            type="password"
            label="Password"
            name="password"
            onChange={handleSignupChange}
          />

          <div style={{ marginTop: 6, marginBottom: 6, width: "100%" }}>
            <PhoneInput
              defaultCountry="us"
              value={phone}
              onChange={handleChange}
            />
          </div>

          <TextField
            fullWidth
            margin="dense"
            type="date"
            label="Date of Birth"
            name="dob"
            onChange={handleSignupChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: new Date().toISOString().split("T")[0], // 🚫 no future dates
            }}
          />

          <TextField
            select
            fullWidth
            margin="dense"
            label="Gender"
            name="gender"
            onChange={handleSignupChange}
          >
            <MenuItem value="MALE">Male</MenuItem>
            <MenuItem value="FEMALE">Female</MenuItem>
            <MenuItem value="OTHER">Other</MenuItem>
          </TextField>

          <TextField
            fullWidth
            margin="dense"
            label="Email"
            name="email"
            onChange={handleSignupChange}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenSignup(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSignupSubmit}
            disabled={
              !form.firstName ||
              !form.lastName ||
              !form.userId ||
              !form.email ||
              !form.password ||
              !form.dob ||
              !form.gender ||
              !localPhone ||
              !countryCode
            }
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
